/*
 * Copyright 2026 Sony Corporation
 */

/**
 * Coalesces rapid updates per key and always delivers the last value.
 *
 * A gizmo drag fires every frame. Without this the editor would issue one write
 * per frame; with a plain debounce the final position could be dropped if the
 * drag ends inside the quiet window, which is the one value that must land.
 */
export interface Throttled<T> {
  push: (key: string, value: T) => void;
  /** Sends the pending value for a key now, if there is one. */
  flush: (key: string) => void;
  cancel: () => void;
}

export interface ThrottleOptions<T> {
  intervalMs: number;
  send: (key: string, value: T) => void;
  now?: () => number;
  schedule?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  unschedule?: (handle: ReturnType<typeof setTimeout>) => void;
}

export const createThrottle = <T>({
  intervalMs,
  send,
  now = Date.now,
  schedule = setTimeout,
  unschedule = clearTimeout,
}: ThrottleOptions<T>): Throttled<T> => {
  const pending = new Map<string, T>();
  const lastSent = new Map<string, number>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  const deliver = (key: string) => {
    const value = pending.get(key);
    if (value === undefined) {
      return;
    }
    pending.delete(key);
    lastSent.set(key, now());
    send(key, value);
  };

  const clearTimer = (key: string) => {
    const timer = timers.get(key);
    if (timer !== undefined) {
      unschedule(timer);
      timers.delete(key);
    }
  };

  return {
    push: (key, value) => {
      pending.set(key, value);

      const elapsed = now() - (lastSent.get(key) ?? -Infinity);
      if (elapsed >= intervalMs) {
        clearTimer(key);
        deliver(key);
        return;
      }

      if (!timers.has(key)) {
        timers.set(
          key,
          schedule(() => {
            timers.delete(key);
            deliver(key);
          }, intervalMs - elapsed)
        );
      }
    },

    flush: (key) => {
      clearTimer(key);
      deliver(key);
    },

    cancel: () => {
      for (const key of [...timers.keys()]) {
        clearTimer(key);
      }
      pending.clear();
    },
  };
};
